import { useCallback, useRef, useState } from "react";
import ConfirmDialog from "@/components/ConfirmDialog";

const EMPTY_DIALOG = {
  open: false,
  title: "",
  description: "",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  variant: "danger",
  testId: "confirm-dialog",
  loading: false,
};

/**
 * Promise-based confirm helper — drop-in replacement for window.confirm.
 *
 *   const { confirm, ConfirmDialogSlot } = useConfirmDialog();
 *   const ok = await confirm({ title: "Delete?", variant: "danger", ... });
 *   if (!ok) return;
 *   ...
 *   return (<>...</><ConfirmDialogSlot />);
 */
export function useConfirmDialog() {
  const [dialog, setDialog] = useState(EMPTY_DIALOG);
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setDialog({
      open: true,
      title: options.title ?? "",
      description: options.description ?? "",
      confirmLabel: options.confirmLabel ?? "Confirm",
      cancelLabel: options.cancelLabel ?? "Cancel",
      variant: options.variant ?? "danger",
      testId: options.testId ?? "confirm-dialog",
      loading: false,
    });
  }), []);

  const finish = useCallback((result) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setDialog(EMPTY_DIALOG);
  }, []);

  const ConfirmDialogSlot = useCallback(() => (
    <ConfirmDialog
      open={dialog.open}
      title={dialog.title}
      description={dialog.description}
      confirmLabel={dialog.confirmLabel}
      cancelLabel={dialog.cancelLabel}
      variant={dialog.variant}
      loading={dialog.loading}
      testId={dialog.testId}
      onCancel={() => finish(false)}
      onConfirm={() => finish(true)}
    />
  ), [dialog, finish]);

  return { confirm, ConfirmDialogSlot };
}
